import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { SidebarCollapsibleSection } from 'components/blocks/layout/sidebar-section';
import { SidebarTreeItem } from 'components/blocks/layout/sidebar-items';
import { useSidebar } from 'components/ui/sidebar';
import { resolveConversationDisplayTitle } from 'utils/chat-utilities';
import { Draggable } from 'features/sidebar/Draggable';
import { SidebarItem } from 'components/blocks/layout/types/sidebar-conversation';
import { useChatStore } from 'state/store/chat/use-chat-store';
import { useCommonStore } from 'state/store/common/useCommonStore';

interface GroupChatsSectionProps {
    conversations: SidebarItem[];
    isSidebarLoading: boolean;
    renderRightAction: (id: string, title: string) => React.ReactNode;
    variant?: 'default' | 'section';
}

export const GroupChatsSection = ({
    conversations,
    isSidebarLoading,
    renderRightAction,
    variant = 'section',
}: GroupChatsSectionProps) => {
    const { pathname } = useLocation();
    const { t } = useTranslation();
    const { isMobile, setOpenMobile } = useSidebar();
    const navigate = useNavigate();
    const hasGroupChatParticipantsArray = useChatStore(
        (state) => state.hasGroupChatParticipantsArray
    );
    const conversationsState = useChatStore((state) => state.conversations);
    const currentUser = useCommonStore((state) => state.user);

    if (!conversations || (conversations.length === 0 && !isSidebarLoading)) return null;

    return (
        <SidebarCollapsibleSection
            title={t("GROUP_CHATS")}
            variant={variant}
        >
            <div className="flex flex-col gap-1">
                {conversations.map((item) => {
                    const displayTitle = resolveConversationDisplayTitle({
                        storedTitle: item.ConversationTitle,
                        participants: hasGroupChatParticipantsArray[item.ConversationId],
                        currentUser,
                        hasManualTitle: conversationsState[item.ConversationId]?.hasManualTitle,
                        isGroupChat: true,
                        fallback: t('NEW_CHAT'),
                    });

                    return (
                    <Draggable key={item.ConversationId} id={item.ConversationId}>
                        <SidebarTreeItem
                            label={displayTitle}
                            variant={variant}
                            isActive={pathname.includes(item.ConversationId)}
                            onClick={() => {
                                if (isMobile) setOpenMobile(false);
                                navigate(`/chat/${item.ConversationId}`);
                            }}
                            rightAction={renderRightAction(item.ConversationId, displayTitle)}
                        />
                    </Draggable>
                    );
                })}
                {isSidebarLoading && (
                    <div className="flex h-10 items-center justify-center py-2">
                        <Loader2 className="h-4 w-4 animate-spin text-[#888888] dark:text-grey-400" />
                    </div>
                )}
            </div>
        </SidebarCollapsibleSection>
    );
};
